import { FC, useRef } from 'react'
import cn from 'classnames'
import DOMPurify from 'dompurify'

import { useMessenger } from '@/hooks/useMessenger'
import {
  CONVERSATION_STATE,
  MESSAGE_TYPES,
  USER_TYPES,
} from '@/context/constants'
import { StoreContextType } from '@/context/types'
import { StoreContext } from '@/context/context'
import { isConversationExpired } from '@/common/utils'
import useAutosizeTextArea from '@/hooks/useAutosizeTextArea'
import useDebounce from '@/hooks/useDebounce'

export interface MessageInputProps {
  context?: React.Context<StoreContextType | null>
  disabled?: boolean
  containerClassName?: string
  textAreaClassName?: string
  buttonClassName?: string
}

const MIN_ROWS = 1

const MessageInput: FC<MessageInputProps> = ({
  context = StoreContext,
  disabled,
  containerClassName,
  textAreaClassName,
  buttonClassName,
}) => {
  const {
    getConfig,
    getInputMessage,
    setInputMessage,
    sendMessage,
    getSelectedConversation,
    findConversation,
    getSendTo,
    getDrafts,
    setDrafts,
  } = useMessenger(context)
  const conversationId = getSelectedConversation()

  const ref = useRef<HTMLTextAreaElement>(null)

  const textInputValue = getInputMessage(conversationId)
  useAutosizeTextArea(ref.current, textInputValue)
  const conversation = findConversation(getSelectedConversation())
  const isExpired = isConversationExpired(conversation)

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(event.target.value)
  }

  const handleSendMessage = () => {
    const participants = conversation?.participants
    const assignedVendor = conversation?.metadata.assignedVendor
    const userType = getConfig()?.userType
    const sendTo = getSendTo()

    // If usertype is vendor, do not send to all vendors
    const filteredParticipants =
      participants?.filter(participant => {
        if (
          userType === USER_TYPES.VENDOR &&
          participant.type === USER_TYPES.VENDOR &&
          sendTo[participant.type] === false
        ) {
          return false
        }

        if (
          assignedVendor !== participant.userExternalId &&
          participant.type === USER_TYPES.VENDOR
        ) {
          return false
        }

        if (
          assignedVendor === participant.userExternalId &&
          sendTo[participant.type] === true &&
          (userType === USER_TYPES.OWNER || userType === USER_TYPES.RENTER)
        ) {
          return true
        }

        return sendTo[participant.type] === true
      }) || []

    const payload = {
      type: MESSAGE_TYPES.MESSAGE,
      conversationId,
      author: {
        type: getConfig()?.userType,
        userExternalId: getConfig()?.userExternalId,
      },
      content: {
        type: 'text',
        text: DOMPurify.sanitize(textInputValue, {
          ALLOWED_TAGS: [],
          ALLOWED_ATTR: [],
        }),
      },
      metadata: {
        source: 'poplar-messenger',
        to: filteredParticipants,
      },
    }

    sendMessage(payload)
    setInputMessage('')

    const draftMessages = getDrafts()
    const newDraftMessages = draftMessages.filter(
      draft => draft.conversationId !== conversationId,
    )
    setDrafts(newDraftMessages)
  }

  if (conversation?.metadata?.state === CONVERSATION_STATE.NEW && isExpired) {
    return null
  }

  return (
    <div
      className={cn(
        'pmui-flex pmui-items-end pmui-gap-0 pmui-px-2 pmui-bg-gray-50 pmui-rounded',
        containerClassName,
      )}
    >
      <textarea
        ref={ref}
        rows={MIN_ROWS}
        value={textInputValue}
        onChange={handleChange}
        className={cn(
          'pmui-max-w-full pmui-w-full pmui-content-center pmui-outline-none \
           pmui-font-normal pmui-text-sm placeholder:pmui-text-black placeholder:pmui-text-sm placeholder:pmui-font-normal \
           placeholder:pmui-opacity-40 pmui-text-opacity-70 \
           focus:pmui-outline-none pmui-transition pmui-duration-200 pmui-ease-out \
           pmui-text-black pmui-bg-gray-50 pmui-border-opacity-0 \
           !pmui-min-h-[44px] pmui-max-h-[120px] pmui-resize-none pmui-overflow-hidden \
           pmui-my-2 pmui-px-2 pmui-py-3',
          textAreaClassName,
        )}
        disabled={disabled}
        placeholder="Write your message here"
      />
      <div className="pmui-flex pmui-justify-end pmui-py-2">
        <button
          className={cn(
            'pmui-btn btn-primary pmui-h-[40px] pmui-w-[60px] pmui-rounded-md pmui-py-1 pmui-px-3 pmui-text-sm pmui-leading-none ',
            buttonClassName,
          )}
          onClick={useDebounce(handleSendMessage, getConfig()?.debounceTimeout)}
          disabled={disabled || !textInputValue}
        >
          Send
        </button>
      </div>
    </div>
  )
}

export default MessageInput
